import { useEffect, useState } from "react";
import firebase from "firebase";
import DB_PATH from "../DB_PATH";

export type PayingMembers = {
  // Whether the community enforces a hard paywall (config.subRequired). When
  // false the analytics dashboard shows raw active users unchanged.
  subRequired: boolean;
  // Member userIds with an active subscription (Members where isSubscribed).
  // The Member doc id is the userId, so this joins to analytics event.userId.
  subscribedIds: Set<string>;
  loading: boolean;
};

// Reads whether a community is a hard paywall and, if so, the set of paying
// member ids. Kept deliberately light: one community-doc read plus (only for
// hard-paywall communities) one indexed Members query, returning just doc ids.
// This lets the dashboard report "active paying users" separately from raw
// active users, which on a hard paywall also include visitors stuck at the
// paywall who never subscribed.
export default function usePayingMembers(communityId?: string): PayingMembers {
  const [state, setState] = useState<PayingMembers>({
    subRequired: false,
    subscribedIds: new Set(),
    loading: true,
  });

  useEffect(() => {
    if (!communityId) {
      setState({ subRequired: false, subscribedIds: new Set(), loading: false });
      return;
    }
    let cancelled = false;
    const reset = () =>
      setState({ subRequired: false, subscribedIds: new Set(), loading: false });

    (async () => {
      try {
        const communityRef = firebase
          .firestore()
          .collection(DB_PATH.COMMUNITY)
          .doc(communityId);
        const communitySnap = await communityRef.get();
        const subRequired =
          (communitySnap.data() as any)?.config?.subRequired === true;

        let subscribedIds = new Set<string>();
        if (subRequired) {
          const membersSnap = await communityRef
            .collection("Members")
            .where("isSubscribed", "==", true)
            .get();
          subscribedIds = new Set(membersSnap.docs.map((doc) => doc.id));
        }

        if (!cancelled) setState({ subRequired, subscribedIds, loading: false });
      } catch {
        if (!cancelled) reset();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [communityId]);

  return state;
}
